import { SyntheticEvent, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'

import BasicTabs from 'components/tabs/basic-tabs/basic-tabs.component'
import TwoColumnLayout from 'layouts/two-column-layout/two-column-layout'
import {
  useGetDriverPayGroupsQuery,
  useLazyGetDriverPayGroupDetailsQuery,
  useUpdateDriverPayGroupStatusMutation,
} from 'store/driver-pay/driver-pay.api'
import { DriverPayGroup, DriverPayStatus } from 'store/driver-pay/driver-pay.types'
import { getAccessToken, getUserId } from 'utils/common'

import DriverPayPreviewDialog from './driver-pay-preview-dialog'
import {
  DEFAULT_DRIVER_MILES_RATE,
  driverPayablesTableHeaders,
  driverPayablesTabs,
} from './driver-payables.constants'

const formatRate = (rate: number) =>
  `$${rate.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const formatAmount = (amount: number) =>
  `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const formatMiles = (miles: number) =>
  miles.toLocaleString('en-US', {
    minimumFractionDigits: miles % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })

const parseRateInput = (value: string) => {
  const parsed = Number.parseFloat(value.replace(/[^0-9.]/g, ''))
  if (!Number.isFinite(parsed) || parsed < 0) return null
  return parsed
}

const groupKey = (group: Pick<DriverPayGroup, 'driverId' | 'year' | 'week'>) =>
  `${group.driverId}|${group.year}|${group.week}`

const DriverPayablesPage = () => {
  const [tabsValue, setTabsValue] = useState(0)
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null)
  const [listSearch, setListSearch] = useState('')
  const [tableSearch, setTableSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [milesRate, setMilesRate] = useState(DEFAULT_DRIVER_MILES_RATE)
  const [rateInput, setRateInput] = useState(formatRate(DEFAULT_DRIVER_MILES_RATE))
  const [previewOpen, setPreviewOpen] = useState(false)
  const [generatingPdf, setGeneratingPdf] = useState(false)
  const [updatingKey, setUpdatingKey] = useState<string | null>(null)

  const isUnpaidTab = tabsValue === 0
  const status: DriverPayStatus = isUnpaidTab ? 'NOT_PAID' : 'PAID'
  const userId = getUserId()

  const { data, isLoading } = useGetDriverPayGroupsQuery(
    { status },
    { skip: !userId }
  )
  const [fetchGroupDetails, detailsState] = useLazyGetDriverPayGroupDetailsQuery()
  const [updateStatus] = useUpdateDriverPayGroupStatusMutation()

  const groups = useMemo(() => data?.groups ?? [], [data?.groups])
  const drivers = useMemo(() => data?.drivers ?? [], [data?.drivers])

  const filteredDrivers = useMemo(() => {
    const query = listSearch.trim().toLowerCase()
    if (!query) return drivers
    return drivers.filter((driver) =>
      (driver.driverName || '').toLowerCase().includes(query)
    )
  }, [drivers, listSearch])

  const filteredInvoices = useMemo(() => {
    let rows = groups
    if (selectedDriverId) {
      rows = rows.filter((group) => group.driverId === selectedDriverId)
    }
    const query = tableSearch.trim().toLowerCase()
    if (!query) return rows
    return rows.filter((group) =>
      [
        group.invoiceNumber,
        group.driverName,
        String(group.week),
        formatRate(group.rate),
        formatMiles(group.miles),
        formatAmount(group.amount),
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    )
  }, [groups, selectedDriverId, tableSearch])

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabsValue(newValue)
    setSelectedDriverId(null)
    setSelectedIds(new Set())
    setTableSearch('')
  }

  const handleRateBlur = () => {
    const parsed = parseRateInput(rateInput)
    if (parsed === null) {
      setRateInput(formatRate(milesRate))
      return
    }
    setMilesRate(parsed)
    setRateInput(formatRate(parsed))
  }

  const handleStatusChange = async (
    group: DriverPayGroup,
    nextStatus: DriverPayStatus
  ) => {
    const key = groupKey(group)
    setUpdatingKey(key)
    try {
      await updateStatus({
        driverId: group.driverId,
        year: group.year,
        week: group.week,
        status: nextStatus,
      }).unwrap()
      setSelectedIds((prev) => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    } finally {
      setUpdatingKey(null)
    }
  }

  const handleView = async (group: DriverPayGroup) => {
    setPreviewOpen(true)
    await fetchGroupDetails({
      driverId: group.driverId,
      year: group.year,
      week: group.week,
    })
  }

  const handleGenerateInvoice = async () => {
    const group = detailsState.data
    if (!group || !userId) return

    setGeneratingPdf(true)
    try {
      const baseUrl = process.env.REACT_APP_ORDER_SERVICE_API
      const params = new URLSearchParams({
        driverId: group.driverId,
        year: String(group.year),
        week: String(group.week),
      })
      const response = await fetch(
        `${baseUrl}/osapi/v1/driver-pay/groups/invoice-pdf?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
            userId,
          },
        }
      )
      if (!response.ok) {
        throw new Error('Failed to generate invoice PDF')
      }
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const newWindow = window.open(blobUrl, '_blank')
      if (newWindow) {
        newWindow.focus()
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000)
      } else {
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = `${group.invoiceNumber}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000)
      }
    } finally {
      setGeneratingPdf(false)
    }
  }

  const toggleSelected = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const allSelected =
    filteredInvoices.length > 0 &&
    filteredInvoices.every((invoice) => selectedIds.has(groupKey(invoice)))

  const selectionPanel = (
    <BasicTabs
      tabs={driverPayablesTabs}
      value={tabsValue}
      handleChange={handleTabChange}
      scrollableContent
    >
      <DriverPayablesList
        search={listSearch}
        onSearchChange={setListSearch}
        drivers={filteredDrivers}
        selectedDriverId={selectedDriverId}
        onSelectDriver={setSelectedDriverId}
      />
    </BasicTabs>
  )

  const detailsPanel = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            Driver Miles Rate
          </Typography>
          <TextField
            size="small"
            value={rateInput}
            onChange={(e) => {
              const next = e.target.value
              setRateInput(next)
              const parsed = parseRateInput(next)
              if (parsed !== null) setMilesRate(parsed)
            }}
            onBlur={handleRateBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur()
              }
            }}
            inputProps={{ 'aria-label': 'Driver miles rate' }}
            sx={{
              width: 96,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                height: 36,
              },
              '& input': {
                color: 'primary.main',
                fontWeight: 600,
                textAlign: 'center',
                py: 0.75,
              },
            }}
          />
        </Stack>
        <TextField
          placeholder="Search"
          size="small"
          value={tableSearch}
          onChange={(e) => setTableSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
      </Stack>

      <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectedIds.size > 0 && !allSelected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(
                          new Set(filteredInvoices.map((invoice) => groupKey(invoice)))
                        )
                      } else {
                        setSelectedIds(new Set())
                      }
                    }}
                  />
                </TableCell>
                {driverPayablesTableHeaders.map((header) => (
                  <TableCell key={header.id}>{header.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      No driver payables found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvoices.map((invoice) => {
                  const key = groupKey(invoice)
                  return (
                    <TableRow key={key} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.has(key)}
                          onChange={(e) =>
                            toggleSelected(key, e.target.checked)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          component="button"
                          onClick={() => handleView(invoice)}
                          sx={{
                            border: 0,
                            background: 'none',
                            color: 'primary.main',
                            cursor: 'pointer',
                            p: 0,
                            font: 'inherit',
                            textDecoration: 'underline',
                          }}
                        >
                          {invoice.invoiceNumber || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{invoice.week}</TableCell>
                      <TableCell>{formatRate(invoice.rate)}</TableCell>
                      <TableCell>{formatMiles(invoice.miles)}</TableCell>
                      <TableCell>{formatAmount(invoice.amount)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleView(invoice)}
                          >
                            View
                          </Button>
                          {isUnpaidTab ? (
                            <Button
                              size="small"
                              variant="contained"
                              disabled={updatingKey === key}
                              onClick={() => handleStatusChange(invoice, 'PAID')}
                            >
                              Mark as Paid
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              disabled={updatingKey === key}
                              onClick={() => handleStatusChange(invoice, 'NOT_PAID')}
                            >
                              Mark as Unpaid
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  )

  return (
    <>
      <TwoColumnLayout>
        {selectionPanel}
        {detailsPanel}
        {selectionPanel}
      </TwoColumnLayout>
      <DriverPayPreviewDialog
        open={previewOpen}
        loading={detailsState.isFetching}
        generating={generatingPdf}
        group={detailsState.data ?? null}
        onClose={() => setPreviewOpen(false)}
        onGenerateInvoice={handleGenerateInvoice}
      />
    </>
  )
}

type DriverPayablesListProps = {
  search: string
  onSearchChange: (value: string) => void
  drivers: { driverId: string; driverName: string }[]
  selectedDriverId: string | null
  onSelectDriver: (driverId: string | null) => void
}

const DriverPayablesList = ({
  search,
  onSearchChange,
  drivers,
  selectedDriverId,
  onSelectDriver,
}: DriverPayablesListProps) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
      p: 1.5,
      gap: 1.5,
    }}
  >
    <TextField
      placeholder="Search"
      size="small"
      fullWidth
      value={search}
      onChange={(e) => onSearchChange(e.target.value)}
    />
    <List sx={{ flex: 1, minHeight: 0, overflow: 'auto', py: 0 }}>
      <ListItemButton
        selected={selectedDriverId === null}
        onClick={() => onSelectDriver(null)}
        sx={{ borderRadius: 1, mb: 0.5 }}
      >
        <ListItemText primary="All" />
      </ListItemButton>
      {drivers.map((driver) => (
        <ListItemButton
          key={driver.driverId}
          selected={selectedDriverId === driver.driverId}
          onClick={() => onSelectDriver(driver.driverId)}
          sx={{ borderRadius: 1, mb: 0.5 }}
        >
          <ListItemText primary={driver.driverName || driver.driverId} />
        </ListItemButton>
      ))}
    </List>
  </Box>
)

export default DriverPayablesPage
